import { types as sdkTypes } from '../../util/sdkLoader';
import { userLocation } from '../../util/maps';
import config from '../../config';
import { currentLocale } from '../../util/locale';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

export const CURRENT_LOCATION_ID = 'current-location';

const GENERATED_BOUNDS_DEFAULT_DISTANCE = 500; // meters
// Distances for generated bounding boxes for different Mapbox place types
const PLACE_TYPE_BOUNDS_DISTANCES = {
  address: 500,
  country: 2000,
  region: 2000,
  postcode: 2000,
  district: 2000,
  place: 2000,
  locality: 2000,
  neighborhood: 2000,
  poi: 2000,
  'poi.landmark': 2000,
};

const EARTH_CIRCUMFERENCE_AT_EQUATOR = 40075017; // meters

// A box `distance` meters from the point in each direction (Mapbox GL's LngLat.toBounds).
export const locationBounds = (latlng, distance) => {
  if (!latlng) {
    return null;
  }

  const latDelta = (360 * distance) / EARTH_CIRCUMFERENCE_AT_EQUATOR;
  const lngDelta = latDelta / Math.cos((Math.PI / 180) * latlng.lat);
  return new SDKLatLngBounds(
    new SDKLatLng(latlng.lat + latDelta, latlng.lng + lngDelta),
    new SDKLatLng(latlng.lat - latDelta, latlng.lng - lngDelta)
  );
};

// The Mapbox SDK loads the first time someone searches for a place, not on every page.
const MAPBOX_SDK_URL = '/static/scripts/mapbox/mapbox-sdk.min.js';
let mapboxSdkLoaded = null;

export const loadMapboxSdk = () => {
  if (window.mapboxSdk) {
    return Promise.resolve(window.mapboxSdk);
  }
  if (!mapboxSdkLoaded) {
    mapboxSdkLoaded = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = MAPBOX_SDK_URL;
      script.async = true;
      script.onload = () => resolve(window.mapboxSdk);
      script.onerror = () => {
        mapboxSdkLoaded = null; // let the next search try again
        reject(new Error('Mapbox SDK failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return mapboxSdkLoaded;
};

const placeOrigin = prediction => {
  if (prediction && Array.isArray(prediction.center) && prediction.center.length === 2) {
    // Coordinates in Mapbox features are represented as [longitude, latitude].
    return new SDKLatLng(prediction.center[1], prediction.center[0]);
  }
  return null;
};

const placeBounds = prediction => {
  if (prediction) {
    if (Array.isArray(prediction.bbox) && prediction.bbox.length === 4) {
      // Bounds in Mapbox features are represented as [minX, minY, maxX, maxY]
      return new SDKLatLngBounds(
        new SDKLatLng(prediction.bbox[3], prediction.bbox[2]),
        new SDKLatLng(prediction.bbox[1], prediction.bbox[0])
      );
    } else {
      // If bounds are not available, generate them around the origin

      // Resolve bounds distance based on place type
      const placeType = Array.isArray(prediction.place_type) && prediction.place_type[0];

      const distance =
        (placeType && PLACE_TYPE_BOUNDS_DISTANCES[placeType]) || GENERATED_BOUNDS_DEFAULT_DISTANCE;

      return locationBounds(placeOrigin(prediction), distance);
    }
  }
  return null;
};

export const GeocoderAttribution = () => null;

/**
 * A forward geocoding (place name -> coordinates) implementation
 * using the Mapbox Geocoding API.
 */
class GeocoderMapbox {
  getClient() {
    if (!this._client) {
      this._client = loadMapboxSdk().then(mapboxSdk =>
        mapboxSdk({ accessToken: config.maps.mapboxAccessToken })
      );
    }
    return this._client;
  }

  // Public API
  //

  /**
   * Search places with the given name.
   *
   * @param {String} search query for place names
   *
   * @return {Promise<{ search: String, predictions: Array<Object>}>}
   * results of the geocoding, should have the original search query
   * and an array of predictions. The format of the predictions is
   * only relevant for the `getPlaceDetails` function below.
   */
  getPlacePredictions(search) {
    const limitCountriesMaybe = config.maps.search.countryLimit
      ? { countries: config.maps.search.countryLimit }
      : {};

    return this.getClient()
      .then(client =>
        client.geocoding
          .forwardGeocode({
            query: search,
            limit: 5,
            language: [currentLocale()],
            types: ['region', 'place'],
            ...limitCountriesMaybe,
          })
          .send()
      )
      .then(response => {
        return {
          search,
          predictions: response.body.features,
        };
      });
  }

  /**
   * Get the ID of the given prediction.
   */
  getPredictionId(prediction) {
    return prediction.id;
  }

  /**
   * Get the address text of the given prediction.
   */
  getPredictionAddress(prediction) {
    if (prediction.predictionPlace) {
      // default prediction defined above
      return prediction.predictionPlace.address;
    }
    // prediction from Mapbox geocoding API
    return prediction.place_name;
  }

  /**
   * Fetch or read place details from the selected prediction.
   *
   * @param {Object} prediction selected prediction object
   *
   * @return {Promise<util.propTypes.place>} a place object
   */
  getPlaceDetails(prediction) {
    if (this.getPredictionId(prediction) === CURRENT_LOCATION_ID) {
      return userLocation().then(latlng => {
        return {
          address: '',
          origin: latlng,
          bounds: locationBounds(latlng, config.maps.search.currentLocationBoundsDistance),
        };
      });
    }

    if (prediction.predictionPlace) {
      return Promise.resolve(prediction.predictionPlace);
    }

    return Promise.resolve({
      address: this.getPredictionAddress(prediction),
      origin: placeOrigin(prediction),
      bounds: placeBounds(prediction),
    });
  }
}

export default GeocoderMapbox;
